/**
 * Состояние приёма лекарства — одно правило на всё приложение.
 *
 * До этого модуля правила были разбросаны по восьми местам и успели разойтись.
 * Лента считала просроченный приём закрытым, календарь — нет, и просроченная
 * доза выглядела в сетке дня предстоящей. Напоминание снималось при любом
 * статусе, кроме `pending`, поэтому кнопка «Отложить» вместо переноса
 * уведомления отменяла его навсегда. Журнал считал отложенный приём
 * предстоящим, а планировщик расписания — нет, и отложенные дозы, выпавшие из
 * курса, оставались в календаре.
 *
 * Здесь же живёт фактическое время приёма: его тоже вычисляли трижды, с разными
 * правилами, и три экрана рассказывали про одно событие разное.
 */

import type { DoseEvent, Medication } from '@/shared/db/schema';
import { type IsoDate, MINUTES_IN_HOUR, type MinutesOfDay, toIsoDate } from '@/shared/lib/datetime';

import { hasDoseSchedule } from './course';

/**
 * Сколько времени после планового у человека есть, чтобы принять лекарство с
 * опозданием и отметить это честно. До конца окна приём не считается
 * пропущенным.
 */
export const MISSED_GRACE_MIN = 2 * MINUTES_IN_HOUR;

/**
 * Спрашивать ли с человека за неотмеченный приём.
 *
 * У курса «по необходимости» назначения нет: приём намечает он сам, чтобы ему
 * напомнили. Не понадобилось — значит, не понадобилось, и красное «Просрочено»
 * тут не про здоровье, а про упрёк. Пропущенным такой приём не становится, в
 * «Незакрытые» не попадает и приверженность лечению не портит.
 */
export function isDoseAccountable(medication: Medication): boolean {
  return hasDoseSchedule(medication);
}

/**
 * Считается ли приём пропущенным.
 *
 * Доза со статусом `pending` становится пропущенной не мгновенно: запись в базе
 * ставится фоновым проходом, а на экранах просрочка досчитывается на лету.
 */
export function isDoseMissed(
  dose: DoseEvent,
  today: IsoDate,
  nowMin: MinutesOfDay,
  graceMin: number = MISSED_GRACE_MIN,
): boolean {
  // Перенесённый приём тоже можно забыть, и отсчёт для него идёт от нового
  // времени. Пока сюда попадал только `pending`, отложенная доза оставалась
  // ожидаемой вечно: ни в «незакрытых» за вчера, ни в пометке «пропущено» её
  // не было — она просто исчезала из дня, занижая приверженность лечению.
  if (dose.status !== 'pending' && dose.status !== 'postponed') return false;
  if (dose.date < today) return true;
  if (dose.date > today) return false;
  return nowMin > expectedDoseMin(dose) + graceMin;
}

/**
 * Состояние приёма с учётом времени.
 *
 * Отличается от `dose.status` тем, что просрочка считается на лету: фоновая
 * пометка «пропущено» ставится только при запуске синхронизации и всегда
 * отстаёт от реальности хотя бы немного.
 */
export type DoseState = 'taken' | 'skipped' | 'missed' | 'postponed' | 'pending';

export function doseState(dose: DoseEvent, today: IsoDate, nowMin: MinutesOfDay): DoseState {
  if (dose.status === 'taken') return 'taken';
  if (dose.status === 'skipped') return 'skipped';
  if (dose.status === 'missed' || isDoseMissed(dose, today, nowMin)) return 'missed';
  return dose.status === 'postponed' ? 'postponed' : 'pending';
}

/**
 * Исход приёма известен: он либо принят, либо не принят окончательно.
 *
 * Просроченный приём тоже закрыт: время вышло, и до отметки задним числом это
 * такой же факт, как отказ. Именно этого не хватало календарю, где просрочка
 * выглядела предстоящим делом.
 */
export function isDoseResolved(dose: DoseEvent, today: IsoDate, nowMin: MinutesOfDay): boolean {
  const state = doseState(dose, today, nowMin);
  return state === 'taken' || state === 'skipped' || state === 'missed';
}

/** Приём, который ещё предстоит: отложенный тоже ждёт своей очереди. */
export function isDoseAhead(dose: DoseEvent): boolean {
  return dose.status === 'pending' || dose.status === 'postponed';
}

/**
 * Время, в которое приём ждут сегодня.
 *
 * Плановое время — назначение врача, и переписывать его нельзя: журнал перестал
 * бы показывать, что было назначено. Но перенос внутри дня — «сегодня выпью не
 * в девять, а в час» — это тоже про ожидание, и все экраны должны знать о нём
 * одинаково: лента, сетка календаря и напоминание.
 *
 * Перенос за полночь к этому дню уже не относится: там своё расписание.
 */
export function expectedDoseMin(dose: DoseEvent): MinutesOfDay {
  if (dose.status !== 'postponed' || dose.snoozedUntil === null) return dose.scheduledMin;

  const moment = new Date(dose.snoozedUntil);
  if (toIsoDate(moment) !== dose.date) return dose.scheduledMin;

  return moment.getHours() * 60 + moment.getMinutes();
}

/**
 * Момент, который попадёт в журнал при отметке «Принял».
 *
 * Обычно это время нажатия — так и есть на самом деле: таблетку выпили сейчас.
 * Опоздание при этом видно честно: приём ждали в девять, отметили в час, и
 * журнал показывает и то, и другое.
 *
 * Но если приём **перенесли на уже прошедшее время**, человек этим и сказал,
 * когда его принял: «выпила утром, отмечаю сейчас». Ставить в журнал момент
 * нажатия — значит спорить с прямым указанием и записывать в медицинский
 * журнал неправду. Перенос вперёд к этому не относится: там приём ещё только
 * ждут, и время нажатия остаётся единственной правдой.
 */
export function takenMomentOf(dose: DoseEvent, now: number = Date.now()): number {
  const moved = dose.status === 'postponed' ? dose.snoozedUntil : null;
  return moved !== null && moved <= now ? moved : now;
}

/**
 * Расхождение с планом, ниже которого фактическое время не показывается.
 *
 * Совпало с точностью до нескольких минут — показывать нечего: плановое время
 * уже стоит в строке, и вторая такая же цифра рядом только шумит.
 */
export const ACTUAL_TIME_THRESHOLD_MIN = 5;

/**
 * Фактическое время приёма, если оно известно и заметно отличается от планового.
 *
 * Отметка, попавшая на другую дату (кнопку нажали уже после полуночи), к этому
 * дню временем суток не относится: сравнивать её с плановым временем нельзя,
 * разница вышла бы бессмысленной.
 */
export function actualDoseMin(
  dose: DoseEvent,
  thresholdMin: number = ACTUAL_TIME_THRESHOLD_MIN,
): MinutesOfDay | null {
  if (dose.status !== 'taken' || dose.takenAt === null) return null;

  const moment = new Date(dose.takenAt);
  if (toIsoDate(moment) !== dose.date) return null;

  const minutes = moment.getHours() * 60 + moment.getMinutes();
  return Math.abs(minutes - dose.scheduledMin) < thresholdMin ? null : minutes;
}

/**
 * Время приёма в сетке дня: фактическое, если известно, иначе ожидаемое.
 *
 * Календарь — запись прошедшего дня: если таблетку выпили в 22:30, блок в 21:00
 * рассказывает о дне неправду. Ровно так же врёт блок на девяти утра у приёма,
 * перенесённого на час дня.
 */
export function doseSlotStart(dose: DoseEvent): MinutesOfDay {
  return actualDoseMin(dose) ?? expectedDoseMin(dose);
}
